import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { chatAPI } from '../services/api';
import './Messages.css';

// ── Static conversation groups ────────────────────────────────────────────────
const BASE_CONVERSATIONS = [
  { id: 'all_students', name: 'All Students Broadcast', initials: 'AS', lastMsg: 'No messages yet', pinned: true },
  { id: 'all_parents',  name: 'All Parents Broadcast',  initials: 'AP', lastMsg: 'No messages yet', pinned: true },
  { id: 'all_teachers', name: 'All Teachers',           initials: 'AT', lastMsg: 'No messages yet', pinned: false },
];

// ── Template data ─────────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: 'holiday',
    label: 'Holiday Messages',
    icon: '📅',
    bg: '#f0fdf4',
    border: '#86efac',
    iconBg: '#dcfce7',
    templates: [
      {
        id: 'h1',
        tag: 'Happy holiday notice',
        text: 'Good morning Parents, {schoolName} wishes you a Happy {holidayName}! School will remain closed on {date}. Thank you.',
        fields: [
          { key: 'holidayName', label: 'Holiday Name', placeholder: 'e.g. Diwali' },
          { key: 'date',        label: 'Date',         placeholder: 'e.g. 12 Nov 2025' },
        ],
      },
      {
        id: 'h2',
        tag: 'Holiday with reason',
        text: 'Dear Parents, {schoolName} announces a holiday on {date} on account of {reason}. Classes will resume on {resumeDate}.',
        fields: [
          { key: 'date',       label: 'Date',        placeholder: 'e.g. 15 Aug 2025' },
          { key: 'reason',     label: 'Reason',      placeholder: 'e.g. Independence Day' },
          { key: 'resumeDate', label: 'Resume Date', placeholder: 'e.g. 16 Aug 2025' },
        ],
      },
      {
        id: 'h3',
        tag: 'Tomorrow is a holiday',
        text: 'Attention Students, {schoolName} informs that tomorrow ({date}) is a holiday. Enjoy your day!',
        fields: [
          { key: 'date', label: 'Date', placeholder: 'e.g. 26 Jan 2026' },
        ],
      },
    ],
  },
  {
    id: 'exam',
    label: 'Exam Messages',
    icon: '📝',
    bg: '#eff6ff',
    border: '#93c5fd',
    iconBg: '#dbeafe',
    templates: [
      {
        id: 'e1',
        tag: 'Exam schedule released',
        text: 'Dear Parents, {schoolName} exam schedule has been released. Exams begin from {startDate}. Please ensure your ward is prepared.',
        fields: [
          { key: 'startDate', label: 'Start Date', placeholder: 'e.g. 10 Mar 2026' },
        ],
      },
      {
        id: 'e2',
        tag: 'Exam dates reminder',
        text: 'Reminder from {schoolName}: {examName} exams are scheduled from {startDate} to {endDate}. Syllabus is available on the portal.',
        fields: [
          { key: 'examName',  label: 'Exam Name',  placeholder: 'e.g. Quarterly' },
          { key: 'startDate', label: 'Start Date', placeholder: 'e.g. 10 Mar 2026' },
          { key: 'endDate',   label: 'End Date',   placeholder: 'e.g. 20 Mar 2026' },
        ],
      },
      {
        id: 'e3',
        tag: 'Hall tickets available',
        text: 'Good morning Parents, {schoolName} informs that hall tickets for {examName} are now available. Please collect from the office.',
        fields: [
          { key: 'examName', label: 'Exam Name', placeholder: 'e.g. Annual' },
        ],
      },
      {
        id: 'e4',
        tag: 'Exam timetable with syllabus',
        text: 'Dear Parents,\n\n📚 *{examName} – Exam Timetable*\n🏫 {schoolName}\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n📅 DATE          | SUBJECT    | SYLLABUS\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n{timetableRows}\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\nPlease ensure your ward is well prepared. 📖\nThank you! 🙏',
        fields: [
          { key: 'examName',      label: 'Exam Name',  placeholder: 'e.g. Half-Yearly Exams' },
          { key: 'timetableRows', label: 'Timetable',  type: 'timetable' },
        ],
      },
    ],
  },
  {
    id: 'fee',
    label: 'Fee Reminders',
    icon: '💰',
    bg: '#fefce8',
    border: '#fde047',
    iconBg: '#fef9c3',
    templates: [
      {
        id: 'f1',
        tag: 'Fee due date reminder',
        text: 'Dear Parent, this is a reminder from {schoolName} that the fee for {term} is due on {dueDate}. Kindly pay to avoid late charges.',
        fields: [
          { key: 'term',    label: 'Term / Period', placeholder: 'e.g. Term 2' },
          { key: 'dueDate', label: 'Due Date',       placeholder: 'e.g. 30 Nov 2025' },
        ],
      },
      {
        id: 'f2',
        tag: 'Pending fee amount',
        text: "Gentle reminder from {schoolName}: Your ward's {feeType} fee of ₹{amount} is pending. Please clear at the earliest.",
        fields: [
          { key: 'feeType', label: 'Fee Type', placeholder: 'e.g. Tuition' },
          { key: 'amount',  label: 'Amount',   placeholder: 'e.g. 5000' },
        ],
      },
    ],
  },
  {
    id: 'general',
    label: 'General Announcements',
    icon: '📢',
    bg: '#faf5ff',
    border: '#c4b5fd',
    iconBg: '#ede9fe',
    templates: [
      {
        id: 'g1',
        tag: 'Message to parents',
        text: 'Good morning Parents, a message from {schoolName}: {customText}',
        fields: [
          { key: 'customText', label: 'Message', placeholder: 'Type your announcement here...' },
        ],
      },
      {
        id: 'g2',
        tag: 'Announcement to students',
        text: 'Dear Students, {schoolName} announces: {customText}. Thank you.',
        fields: [
          { key: 'customText', label: 'Announcement', placeholder: 'Type your announcement here...' },
        ],
      },
    ],
  },
  {
    id: 'custom',
    label: 'Custom Message',
    icon: '✏️',
    bg: '#fff7ed',
    border: '#fdba74',
    iconBg: '#fed7aa',
    templates: [],
  },
];

// ── Timetable Row Builder ─────────────────────────────────────────────────────

const EMPTY_ROW = () => ({ date: '', subject: '', syllabus: '' });

function formatDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return d && m && y ? `${d}/${m}/${y}` : isoDate;
}

function buildRowString(row) {
  const parts = [formatDate(row.date) || row.date, row.subject];
  if (row.syllabus) parts.push(row.syllabus);
  return parts.join(' | ');
}

function TimetableBuilder({ value, onChange }) {
  const parseRows = (val) => {
    if (!val) return [EMPTY_ROW()];
    const lines = val.split('\n').filter(Boolean);
    if (!lines.length) return [EMPTY_ROW()];
    return lines.map((line) => {
      const [date = '', subject = '', syllabus = ''] = line.split(' | ');
      return { date, subject, syllabus };
    });
  };

  const [rows, setRows] = React.useState(() => parseRows(value));

  const commit = (updated) => {
    setRows(updated);
    onChange(updated.map(buildRowString).join('\n'));
  };

  const updateRow = (idx, field, val) =>
    commit(rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));

  const addRow = () => commit([...rows, EMPTY_ROW()]);

  const removeRow = (idx) => {
    const updated = rows.filter((_, i) => i !== idx);
    commit(updated.length ? updated : [EMPTY_ROW()]);
  };

  return (
    <div className="msgs-tt-builder">
      <div className="msgs-tt-head">
        <span className="msgs-tt-h msgs-tt-h-date">Date</span>
        <span className="msgs-tt-h msgs-tt-h-subj">Subject</span>
        <span className="msgs-tt-h msgs-tt-h-syll">Syllabus / Topic</span>
        <span className="msgs-tt-h msgs-tt-h-del" />
      </div>
      {rows.map((row, idx) => (
        <div key={idx} className="msgs-tt-row">
          <input
            className="msgs-tt-cell msgs-tt-cell-date"
            type="date"
            value={row.date}
            onChange={(e) => updateRow(idx, 'date', e.target.value)}
            title="Date"
          />
          <input
            className="msgs-tt-cell msgs-tt-cell-subj"
            placeholder="ENGLISH"
            value={row.subject}
            onChange={(e) => updateRow(idx, 'subject', e.target.value.toUpperCase())}
          />
          <input
            className="msgs-tt-cell msgs-tt-cell-syll"
            placeholder="Topic / Chapter"
            value={row.syllabus}
            onChange={(e) => updateRow(idx, 'syllabus', e.target.value)}
          />
          <button
            className="msgs-tt-del"
            onClick={() => removeRow(idx)}
            title="Remove"
            type="button"
          >✕</button>
        </div>
      ))}
      <button className="msgs-tt-add" onClick={addRow} type="button">+ Add Row</button>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildPreview(templateText, schoolName, fieldValues) {
  let out = templateText.replace(/\{schoolName\}/g, schoolName);
  Object.entries(fieldValues).forEach(([key, val]) => {
    out = out.replace(new RegExp(`\\{${key}\\}`, 'g'), val || `{${key}}`);
  });
  return out;
}

function PreviewContent({ text }) {
  const parts = text.split(/(\{[^}]+\})/g);
  return (
    <>
      {parts.map((part, i) =>
        /^\{[^}]+\}$/.test(part)
          ? <mark key={i} className="msgs-highlight">{part}</mark>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Messages({ schoolName: propSchoolName }) {
  const schoolName = propSchoolName || sessionStorage.getItem('gs_school_name') || 'School';

  // Conversations (we hydrate lastMsg with sent messages)
  const [conversations, setConversations] = useState(BASE_CONVERSATIONS);
  const [selectedConv, setSelectedConv]   = useState(null);
  const [search, setSearch]               = useState('');
  const [filter, setFilter]               = useState('all');

  // Template picker modal
  const [showPicker, setShowPicker]               = useState(false);
  const [pickerStep, setPickerStep]               = useState('categories');
  const [selectedCategory, setSelectedCategory]   = useState(null);
  const [selectedTemplate, setSelectedTemplate]   = useState(null);
  const [fieldValues, setFieldValues]             = useState({});
  const [composeText, setComposeText]             = useState('');
  const [recipient, setRecipient]                 = useState('all_parents');
  const [sent, setSent]                           = useState(false);
  const [sending, setSending]                     = useState(false);
  const [sendError, setSendError]                 = useState('');

  // ── Filtered conversation list ──

  const filtered = useMemo(() => {
    let list = conversations;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q));
    }
    if (filter === 'pinned') list = list.filter((c) => c.pinned);
    return list;
  }, [conversations, search, filter]);

  // ── Modal helpers ──

  const openPicker = () => {
    setPickerStep('categories');
    setSelectedCategory(null);
    setSelectedTemplate(null);
    setFieldValues({});
    setComposeText('');
    setSent(false);
    setSendError('');
    setShowPicker(true);
  };

  const closePicker = () => setShowPicker(false);

  const selectCategory = (cat) => {
    setSelectedCategory(cat);
    if (cat.id === 'custom') {
      setSelectedTemplate(null);
      setFieldValues({});
      setComposeText('');
      setPickerStep('compose');
    } else {
      setPickerStep('templates');
    }
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    const init = {};
    tpl.fields.forEach((f) => { init[f.key] = ''; });
    setFieldValues(init);
    setPickerStep('compose');
  };

  const goBack = () => {
    if (pickerStep === 'templates') setPickerStep('categories');
    else if (pickerStep === 'compose') {
      if (selectedCategory?.id === 'custom') setPickerStep('categories');
      else setPickerStep('templates');
    }
  };

  const handleFieldChange = (key, val) =>
    setFieldValues((prev) => ({ ...prev, [key]: val }));

  const previewText = selectedTemplate
    ? buildPreview(selectedTemplate.text, schoolName, fieldValues)
    : composeText;

  const canSend = selectedTemplate ? true : composeText.trim().length > 0;

  const RECIPIENT_THREAD_KEY = {
    all_students: 'bc:students',
    all_parents:  'bc:parents',
    all_teachers: 'bc:teachers',
  };

  const handleSend = async () => {
    if (!canSend || sending) return;
    setSending(true);
    setSendError('');
    const thread_key   = RECIPIENT_THREAD_KEY[recipient] || recipient;
    const thread_title = BASE_CONVERSATIONS.find((c) => c.id === recipient)?.name || recipient;
    try {
      await chatAPI.sendWhatsapp(thread_key, previewText, thread_title);
      const preview = `${previewText.slice(0, 50)}${previewText.length > 50 ? '…' : ''}`;
      setConversations((prev) =>
        prev.map((c) => c.id === recipient ? { ...c, lastMsg: preview } : c)
      );
      setSent(true);
      setTimeout(() => { closePicker(); setSent(false); }, 2000);
    } catch (err) {
      setSendError(err.response?.data?.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const modalTitle = () => {
    if (pickerStep === 'categories') return 'Choose Template Category';
    if (pickerStep === 'templates') return `${selectedCategory?.icon} ${selectedCategory?.label}`;
    return 'Compose Message';
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="msgs-page page">
      <div className="msgs-layout">

        {/* ── Left Panel: Chat List ── */}
        <aside className="msgs-panel">

          {/* Dark header */}
          <div className="msgs-panel-header">
            <span className="msgs-panel-title">💬 Messages</span>
            <button className="msgs-new-pill" onClick={openPicker}>+ New</button>
          </div>

          {/* Search */}
          <div className="msgs-search-wrap">
            <span className="msgs-search-icon">🔍</span>
            <input
              className="msgs-search-input"
              placeholder="Search chats..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="msgs-search-clear" onClick={() => setSearch('')}>✕</button>
            )}
          </div>

          {/* Filter dropdown */}
          <div className="msgs-filter-wrap">
            <select
              className="msgs-filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Conversations</option>
              <option value="pinned">Pinned</option>
            </select>
            <span className="msgs-filter-arrow">▾</span>
          </div>

          {/* Helper hint */}
          <div className="msgs-filter-hint">Pick a category to filter</div>

          {/* Conversation list */}
          <ul className="msgs-conv-list">
            {filtered.length === 0 && (
              <li className="msgs-no-results">No conversations found</li>
            )}
            {filtered.map((conv) => (
              <li
                key={conv.id}
                className={`msgs-conv-item${selectedConv === conv.id ? ' active' : ''}`}
                onClick={() => setSelectedConv(conv.id)}
              >
                <div className="msgs-avatar">{conv.initials}</div>
                <div className="msgs-conv-info">
                  <span className="msgs-conv-name">{conv.name}</span>
                  <span className="msgs-conv-preview">{conv.lastMsg}</span>
                </div>
                {conv.pinned && <span className="msgs-pin-icon">📌</span>}
              </li>
            ))}
          </ul>
        </aside>

        {/* ── Right: Chat / Placeholder area ── */}
        <main className="msgs-main">
          {selectedConv ? (
            <div className="msgs-chat-area">
              <div className="msgs-chat-header">
                <div className="msgs-avatar msgs-avatar-sm">
                  {conversations.find((c) => c.id === selectedConv)?.initials}
                </div>
                <div>
                  <div className="msgs-chat-name">
                    {conversations.find((c) => c.id === selectedConv)?.name}
                  </div>
                  <div className="msgs-chat-sub">Broadcast channel</div>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  style={{ marginLeft: 'auto' }}
                  onClick={openPicker}
                >
                  + New Message
                </button>
              </div>
              <div className="msgs-chat-body">
                <div className="msgs-placeholder-area">
                  <div className="msgs-placeholder-bubble">💬</div>
                  <div className="msgs-placeholder-title">Internal Communication</div>
                  <div className="msgs-placeholder-sub">
                    Pick a category on the left, then select a conversation to start messaging.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="msgs-placeholder-area">
              <div className="msgs-placeholder-bubble">💬</div>
              <div className="msgs-placeholder-title">Internal Communication</div>
              <div className="msgs-placeholder-sub">
                Pick a category on the left, then select a conversation to start messaging.
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ── Template Picker Modal ── */}
      {showPicker && createPortal(
        <div
          className="modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closePicker(); }}
        >
          <div className="modal-box msgs-picker-modal" style={{ maxWidth: 600 }}>

            {/* Header */}
            <div className="msgs-modal-header">
              <div className="msgs-modal-title">
                {pickerStep !== 'categories' && (
                  <button className="msgs-back-btn" onClick={goBack}>←</button>
                )}
                {modalTitle()}
              </div>
              <button className="msgs-close-btn" onClick={closePicker}>✕</button>
            </div>

            <div className="msgs-modal-body">

              {/* Step 1: Categories */}
              {pickerStep === 'categories' && (
                <div className="msgs-cat-grid">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      className="msgs-cat-card"
                      style={{ background: cat.bg, borderColor: cat.border }}
                      onClick={() => selectCategory(cat)}
                    >
                      <span className="msgs-cat-icon" style={{ background: cat.iconBg }}>
                        {cat.icon}
                      </span>
                      <span className="msgs-cat-label">{cat.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2: Templates */}
              {pickerStep === 'templates' && selectedCategory && (
                <div className="msgs-tpl-list">
                  {selectedCategory.templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      className="msgs-tpl-card"
                      onClick={() => selectTemplate(tpl)}
                    >
                      <span className="msgs-tpl-tag">{tpl.tag}</span>
                      <span className="msgs-tpl-text">
                        {tpl.text.replace(/\{schoolName\}/g, schoolName)}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Step 3: Compose */}
              {pickerStep === 'compose' && (
                <div className="msgs-compose">

                  {/* To */}
                  <div className="msgs-field-row">
                    <label className="msgs-field-label">To</label>
                    <select
                      className="msgs-field-select"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                    >
                      {BASE_CONVERSATIONS.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* School name (locked) */}
                  <div className="msgs-field-row">
                    <label className="msgs-field-label">School Name</label>
                    <span className="msgs-school-chip" title="Auto-filled from your school profile">
                      🏫 {schoolName}
                    </span>
                  </div>

                  <hr className="msgs-divider" />

                  {/* Dynamic fields */}
                  {selectedTemplate && selectedTemplate.fields.map((f) => (
                    <div key={f.key} className={`msgs-field-row${f.type === 'timetable' ? ' msgs-field-row--top' : ''}`}>
                      <label className="msgs-field-label">{f.label}</label>
                      {f.type === 'timetable' ? (
                        <TimetableBuilder
                          value={fieldValues[f.key] || ''}
                          onChange={(val) => handleFieldChange(f.key, val)}
                        />
                      ) : (
                        <input
                          className="msgs-field-input"
                          placeholder={f.placeholder}
                          value={fieldValues[f.key] || ''}
                          onChange={(e) => handleFieldChange(f.key, e.target.value)}
                        />
                      )}
                    </div>
                  ))}

                  {/* Preview / Textarea */}
                  <div>
                    <div className="msgs-preview-label">
                      {selectedTemplate ? 'Message Preview' : 'Your Message'}
                    </div>
                    {selectedTemplate ? (
                      <div className="msgs-preview-box">
                        <PreviewContent text={previewText} />
                      </div>
                    ) : (
                      <textarea
                        className="msgs-textarea"
                        placeholder={`Type a custom message from ${schoolName}...`}
                        value={composeText}
                        onChange={(e) => setComposeText(e.target.value)}
                        rows={5}
                      />
                    )}
                  </div>

                  {/* Sent banner */}
                  {sent && (
                    <div className="msgs-sent-banner">
                      ✓ Message sent to{' '}
                      {BASE_CONVERSATIONS.find((c) => c.id === recipient)?.name}!
                    </div>
                  )}

                  {/* Error banner */}
                  {sendError && (
                    <div className="msgs-error-banner">{sendError}</div>
                  )}

                  {/* Actions */}
                  {!sent && (
                    <div className="msgs-actions">
                      <button className="btn btn-secondary" onClick={closePicker} disabled={sending}>
                        Cancel
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={handleSend}
                        disabled={!canSend || sending}
                      >
                        {sending ? 'Sending…' : '📤 Send Message'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
